import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useLanguage } from "@/components/ui/LanguageContext";
import { motion } from "framer-motion";
import {
  ChevronRight,
  Circle,
  CheckCircle2,
  CalendarDays,
  BarChart3,
  PlayCircle,
  Target,
  Clock,
} from "lucide-react";
import VideoCard from "@/components/common/VideoCard";
import MainCampusOnly, { isCorrespondenceUser } from "@/components/common/MainCampusOnly";
import { Skeleton } from "@/components/ui/skeleton";
import { getCurrentUser } from "@/api/auth";
import {
  getDailyPostStatus,
  getDiscordReactionStats,
  getStampCardPoints,
  getVideoDetail,
  listVideos,
  listUserProgress,
} from "@/api/content";
import { useRequireAuthRedirect } from "@/hooks/useRequireAuthRedirect";
import { SERIES_FILTER_HIDDEN } from "@/config/catalogFilters";

const HOME_VIDEO_PREVIEW_COUNT = 3;
const HOME_NEW_FETCH_COUNT = 20;
const HOME_CONTINUE_WATCHING_COUNT = 10;
const HOME_VIDEO_SCROLL_CLASS =
  "-mx-3 flex snap-x snap-mandatory gap-4 overflow-x-auto px-3 pb-3 md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0 md:pb-0";
const HOME_VIDEO_SCROLL_ITEM_CLASS = "w-[82vw] max-w-sm flex-none snap-start md:w-auto md:max-w-none";
const HOME_CONTINUE_SCROLL_CLASS =
  "-mx-3 flex snap-x snap-mandatory gap-4 overflow-x-auto px-3 pb-3 md:mx-0 md:px-0";
const HOME_CONTINUE_SCROLL_ITEM_CLASS =
  "w-[82vw] max-w-sm flex-none snap-start md:w-[calc((100%-2rem)/3)] md:max-w-none";

// TODO: レイアウト確認後は false に戻す
const USE_DISCORD_REACTION_PREVIEW = false;
const PREVIEW_DISCORD_REACTION_LEADERS = [
  {
    id: "preview-1",
    serverName: "サンプル太郎",
    reactionCount: 42,
    avatarUrl: "https://cdn.discordapp.com/avatars/invalid/broken.png",
  },
  { id: "preview-2", serverName: "サンプル花子", reactionCount: 31, avatarUrl: null },
  {
    id: "preview-3",
    serverName: "サンプル次郎",
    reactionCount: 24,
    avatarUrl: "https://cdn.discordapp.com/embed/avatars/1.png",
  },
  {
    id: "preview-4",
    serverName: "サンプル三郎",
    reactionCount: 18,
    avatarUrl: "https://cdn.discordapp.com/avatars/0/does-not-exist.png",
  },
  {
    id: "preview-5",
    serverName: "サンプル美咲",
    reactionCount: 12,
    avatarUrl: "https://cdn.discordapp.com/embed/avatars/2.png",
  },
];
const PREVIEW_DISCORD_REACTION_EMOJIS = [
  { id: "preview-emoji-1", emojiDisplay: "👍", emojiName: "thumbsup", reactionCount: 18 },
  { id: "preview-emoji-2", emojiDisplay: "🔥", emojiName: "fire", reactionCount: 14 },
  { id: "preview-emoji-3", emojiDisplay: "✨", emojiName: "sparkles", reactionCount: 11 },
  { id: "preview-emoji-4", emojiDisplay: "🎨", emojiName: "art", reactionCount: 9 },
  { id: "preview-emoji-5", emojiDisplay: "❤️", emojiName: "heart", reactionCount: 7 },
];

function ReactionLeaderAvatar({ avatarUrl, serverName }) {
  const [hasError, setHasError] = useState(false);
  const initial = (serverName || "?").slice(0, 1);

  if (!avatarUrl || hasError) {
    return (
      <div className="h-8 w-8 rounded-full bg-[#8b6f47] text-[#f5ede3] flex items-center justify-center text-sm font-semibold ring-2 ring-white">
        {initial}
      </div>
    );
  }

  return (
    <img
      src={avatarUrl}
      alt=""
      className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
      onError={() => setHasError(true)}
    />
  );
}

export default function Home() {
  const { t } = useLanguage();
  const { canRenderProtectedContent, isLoadingAuth } = useRequireAuthRedirect();
  const [user, setUser] = useState(null);
  const displayName =
    user?.display_name ||
    user?.global_name ||
    user?.full_name ||
    user?.discord_username ||
    user?.username ||
    "User";
  const isCorrespondenceStudent = isCorrespondenceUser(user);
  const canLoadMainCampusFeatures = !!user?.id && !isCorrespondenceStudent;

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const userData = await getCurrentUser();
      if (isMounted && userData) setUser(userData);
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const { data: videoResponse, isLoading: newVideosLoading } = useQuery({
    queryKey: ["videos", "home-new", HOME_VIDEO_PREVIEW_COUNT, HOME_NEW_FETCH_COUNT],
    queryFn: async () => {
      return listVideos({
        page: 1,
        pageSize: HOME_NEW_FETCH_COUNT,
        sort: "newest",
      });
    },
    enabled: canRenderProtectedContent,
  });

  const newVideos = useMemo(
    () =>
      (videoResponse?.items ?? [])
        .filter((video) => !SERIES_FILTER_HIDDEN.has(String(video.category_id ?? "")))
        .slice(0, HOME_VIDEO_PREVIEW_COUNT),
    [videoResponse]
  );

  const { data: progress = [], isLoading: progressLoading } = useQuery({
    queryKey: ["progress", user?.id],
    queryFn: async () => {
      return listUserProgress();
    },
    enabled: !!user?.id,
  });
  const progressByVideoId = useMemo(
    () => new Map(progress.map((item) => [String(item.video_id), item])),
    [progress]
  );

  const continueWatchingProgress = useMemo(
    () =>
      progress
        .filter((p) => !p.is_completed && p.progress_seconds > 0)
        .slice(0, HOME_CONTINUE_WATCHING_COUNT),
    [progress]
  );
  const continueWatchingIds = continueWatchingProgress.map((p) => p.video_id).join(",");
  const { data: continueWatching = [], isLoading: continueWatchingDetailsLoading } = useQuery({
    queryKey: ["videos", "home-continue", continueWatchingIds],
    queryFn: async () => {
      const videos = await Promise.all(
        continueWatchingProgress.map(async (p) => {
          try {
            const video = await getVideoDetail(p.video_id);
            return { ...video, progress: p };
          } catch {
            return null;
          }
        })
      );
      return videos.filter(Boolean);
    },
    enabled: continueWatchingProgress.length > 0,
  });
  const continueWatchingLoading = progressLoading || continueWatchingDetailsLoading;

  const {
    data: dailyPostStatus,
    isLoading: dailyPostStatusLoading,
    error: dailyPostStatusError,
  } = useQuery({
    queryKey: ["daily-post-status", user?.id],
    queryFn: getDailyPostStatus,
    enabled: canLoadMainCampusFeatures,
  });

  const {
    data: discordReactionStats,
    isLoading: discordReactionStatsLoading,
    error: discordReactionStatsError,
  } = useQuery({
    queryKey: ["discord-reaction-stats", user?.id, 30],
    queryFn: () => getDiscordReactionStats({ days: 30, limit: 5 }),
    enabled: canLoadMainCampusFeatures,
  });

  const stampRallyState = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const monthLabel = `${year}年 ${month + 1}月`;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const sundayFirstOffset = new Date(year, month, 1).getDay();
    return { year, month: month + 1, monthLabel, daysInMonth, sundayFirstOffset };
  }, []);

  const {
    data: stampCardPoints = [],
    isLoading: stampCardPointsLoading,
    error: stampCardPointsError,
  } = useQuery({
    queryKey: ["stamp-card-points", user?.id, stampRallyState.year, stampRallyState.month],
    queryFn: () => getStampCardPoints({ year: stampRallyState.year, month: stampRallyState.month }),
    enabled: canLoadMainCampusFeatures,
  });

  const defaultPostStatusItems = [
    { id: "gallery", label: "展示室", current: 0, target: 1 },
    { id: "break-room", label: "休憩室", current: 0, target: 2 },
    { id: "practice-upload", label: "自主練アップ部屋", current: 0, target: 2 },
    { id: "to-do", label: "to_do部屋", current: 0, target: 1 },
  ];
  const postStatusItems = dailyPostStatus?.items?.length
    ? dailyPostStatus.items
    : defaultPostStatusItems;
  const completedPostStatusCount = postStatusItems.filter((item) => item.current >= item.target).length;
  const totalPostCount = postStatusItems.reduce((sum, item) => sum + item.current, 0);
  const targetPostCount = postStatusItems.reduce((sum, item) => sum + item.target, 0);
  const postStatusProgressPercent =
    targetPostCount > 0 ? Math.min(100, Math.round((totalPostCount / targetPostCount) * 100)) : 0;
  const discordReactionLeaders = USE_DISCORD_REACTION_PREVIEW
    ? PREVIEW_DISCORD_REACTION_LEADERS
    : (discordReactionStats?.leaders ?? []);
  const discordReactionEmojis = USE_DISCORD_REACTION_PREVIEW
    ? PREVIEW_DISCORD_REACTION_EMOJIS
    : (discordReactionStats?.emojis ?? []);
  const isDiscordReactionLoading = discordReactionStatsLoading && !USE_DISCORD_REACTION_PREVIEW;
  const getApiErrorStatus = (error) => {
    const status = error?.response?.status ?? error?.message ?? null;
    const code = error?.response?.data?.code;
    const detail = error?.response?.data?.detail;
    return [status, code, detail].filter(Boolean).join(" / ");
  };

  const stampPointByDay = useMemo(() => {
    return stampCardPoints.reduce((map, item) => {
      if (!item.day) return map;
      map.set(item.day, (map.get(item.day) ?? 0) + item.pointAmount);
      return map;
    }, new Map());
  }, [stampCardPoints]);
  const stampActiveDayCount = [...stampPointByDay.values()].filter((points) => points > 0).length;
  const stampMonthlyPointTotal = [...stampPointByDay.values()].reduce((sum, points) => sum + points, 0);

  const stampPointLegend = [
    { label: "0", swatchClass: "bg-white/70 border-[#efe5d8]" },
    { label: "1–150", swatchClass: "bg-emerald-100 border-emerald-200" },
    { label: "151–299", swatchClass: "bg-emerald-400 border-emerald-500" },
    { label: "300+", swatchClass: "bg-emerald-600 border-emerald-700" },
  ];

  const getStampDayClassName = (points) => {
    if (points >= 300) return "bg-emerald-600 text-white border-emerald-700 font-semibold";
    if (points >= 151) return "bg-emerald-400 text-white border-emerald-500 font-semibold";
    if (points >= 1) return "bg-emerald-100 text-emerald-900 border-emerald-200 font-semibold";
    return "bg-white/70 text-[#6b5540] border-[#efe5d8]";
  };

  const dashboardCardClassName =
    "xl:col-span-4 xl:bg-white/95 xl:border xl:border-[#e8dccd] xl:rounded-2xl xl:p-4 xl:shadow-sm flex flex-col xl:h-full";

  const stampCells = [
    ...Array.from({ length: stampRallyState.sundayFirstOffset }, (_, i) => ({ key: `blank-${i}`, day: null })),
    ...Array.from({ length: stampRallyState.daysInMonth }, (_, i) => ({ key: `day-${i + 1}`, day: i + 1 })),
  ];

  const VideoSkeleton = () => (
    <div className="space-y-3">
      <Skeleton className="aspect-video rounded-xl bg-[#d4c4b0]" />
      <Skeleton className="h-4 w-3/4 bg-[#d4c4b0]" />
      <Skeleton className="h-3 w-1/2 bg-[#d4c4b0]" />
    </div>
  );

  const SectionHeader = ({ title, icon: Icon, linkTo }) => (
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-xl font-bold flex items-center gap-2 text-[#2d1810]">
        <Icon className="w-5 h-5 text-[#8b6f47]" />
        {title}
      </h2>
      {linkTo && (
        <Link
          to={createPageUrl(linkTo)}
          className="text-sm text-[#6b5540] hover:text-[#3d2817] flex items-center gap-1 transition-colors"
        >
          {t("all")}
          <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );

  if (isLoadingAuth || !canRenderProtectedContent) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810] pb-20">
      <div className="w-full max-w-[1920px] mx-auto px-3 sm:px-4 lg:px-11 2xl:px-12 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2 text-[#2d1810]">
            {user ? `${displayName}さん、おかえりなさい` : t("home")}
          </h1>
          <p className="text-[#5d4529]">{t("heroSubtitle")}</p>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <MainCampusOnly locked={isCorrespondenceStudent}>
            <div className="grid grid-cols-1 xl:grid-cols-12 xl:items-stretch gap-4">
              <div className={dashboardCardClassName}>
              <h3 className="font-semibold text-[#2d1810] flex items-center gap-2">
                <span className="h-8 w-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <Target className="w-4 h-4" />
                </span>
                投稿状況
              </h3>
              {dailyPostStatusError && (
                <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">
                  投稿状況APIの取得に失敗しました: {getApiErrorStatus(dailyPostStatusError)}
                </p>
              )}
              {!dailyPostStatusError && !dailyPostStatusLoading && dailyPostStatus?.row_found === false && (
                <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  daily_table に現在のDiscord IDの行が見つかりません。
                </p>
              )}
              <div className="mt-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-[#faf6f1] border border-emerald-100 p-4">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="mt-1 text-3xl font-bold text-[#2d1810]">
                      {totalPostCount}
                      <span className="text-base font-semibold text-[#8a775f]">/{targetPostCount}</span>
                    </p>
                  </div>
                  <span className="rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white">
                    {completedPostStatusCount}/{postStatusItems.length} 部屋
                  </span>
                </div>
                <div className="mt-4 h-2.5 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${postStatusProgressPercent}%` }}
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-1 flex-col gap-2">
                {postStatusItems.map((item) => {
                  const isDone = item.current >= item.target;
                  return (
                    <div key={item.id} className="flex flex-1 items-center justify-between rounded-xl bg-[#faf6f1] px-3 py-2.5 text-sm">
                      <div className="min-w-0 flex items-center gap-2 pr-2 text-[#5d4529]">
                        {isDone ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-[#d4c4b0] shrink-0" />
                        )}
                        <span className="truncate font-medium">{item.label}</span>
                      </div>
                      <span className={isDone ? "text-emerald-600 font-semibold" : "text-[#8a775f]"}>
                        {item.current}/{item.target}
                      </span>
                    </div>
                  );
                })}
              </div>
              </div>

              <div className={dashboardCardClassName}>
              <h3 className="font-semibold text-[#2d1810] flex items-center gap-2">
                <span className="h-8 w-8 rounded-full bg-[#f5ede3] text-[#8b6f47] flex items-center justify-center">
                  <CalendarDays className="w-4 h-4" />
                </span>
                学習スタンプカレンダー
              </h3>
              <div className="mt-4 rounded-2xl bg-[#faf6f1] border border-[#efe5d8] p-3 sm:p-4 flex-1 flex flex-col min-h-0">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-[#2d1810]">{stampRallyState.monthLabel}</p>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#6b5540]">
                    {stampActiveDayCount}日 / {stampMonthlyPointTotal}pt
                  </span>
                </div>
                {stampCardPointsError && (
                  <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">
                    スタンプカードAPIの取得に失敗しました: {getApiErrorStatus(stampCardPointsError)}
                  </p>
                )}
                {stampCardPointsLoading && (
                  <p className="mb-3 rounded-xl bg-white px-3 py-2 text-xs text-[#6b5540]">
                    スタンプカードを読み込み中です。
                  </p>
                )}
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-[#8a775f] mb-1.5">
                  {["日", "月", "火", "水", "木", "金", "土"].map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                </div>
                <div className="grid flex-1 grid-cols-7 auto-rows-fr gap-1 min-h-[220px]">
                  {stampCells.map((cell) => {
                    const pointAmount = cell.day == null ? 0 : (stampPointByDay.get(cell.day) ?? 0);
                    return (
                      <div
                        key={cell.key}
                        title={pointAmount > 0 ? `${pointAmount}ポイント` : undefined}
                        className={`min-h-8 rounded-md text-[11px] flex items-center justify-center border ${
                          cell.day == null
                            ? "border-transparent bg-transparent"
                            : getStampDayClassName(pointAmount)
                        }`}
                      >
                        {cell.day ?? ""}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center justify-between gap-1 rounded-xl bg-white/80 px-2.5 py-2">
                  {stampPointLegend.map((item) => (
                    <div key={item.label} className="flex items-center gap-1 text-[10px] text-[#5d4529]">
                      <span
                        className={`h-2.5 w-2.5 shrink-0 rounded-sm border ${item.swatchClass}`}
                        aria-hidden="true"
                      />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              </div>

              <div className={dashboardCardClassName}>
              <h3 className="font-semibold text-[#2d1810] flex items-center gap-2">
                <span className="h-8 w-8 rounded-full bg-[#f5ede3] text-[#8b6f47] flex items-center justify-center">
                  <BarChart3 className="w-4 h-4" />
                </span>
                Discord リアクションランキング
              </h3>
              {!USE_DISCORD_REACTION_PREVIEW && discordReactionStatsError && (
                <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">
                  リアクションAPIの取得に失敗しました: {getApiErrorStatus(discordReactionStatsError)}
                </p>
              )}
              <div className="mt-4 rounded-2xl border border-[#efe5d8] bg-gradient-to-br from-[#faf6f1] to-white p-3 flex-1 flex flex-col">
                <p className="mb-3 text-xs text-[#6b5540]">直近30日であなたの投稿に反応してくれた上位5名</p>
                <div className="flex flex-1 flex-col gap-2">
                  {isDiscordReactionLoading ? (
                    <div className="rounded-xl bg-white px-3 py-4 text-sm text-[#6b5540]">
                      リアクションを読み込み中です。
                    </div>
                  ) : discordReactionLeaders.length > 0 ? (
                    discordReactionLeaders.map((leader, index) => (
                      <div
                        key={`${leader.id || "leader"}-${index}`}
                        className={`flex flex-1 items-center gap-3 rounded-xl px-3 py-2 ${
                          index === 0 ? "bg-[#f3ead9] ring-1 ring-[#d9c8ad]" : "bg-white"
                        }`}
                      >
                        <span className="w-6 text-center text-sm font-bold text-[#8b6f47]">{index + 1}</span>
                        <ReactionLeaderAvatar
                          avatarUrl={leader.avatarUrl}
                          serverName={leader.serverName}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#2d1810]">
                          {leader.serverName}
                        </span>
                        <span className="text-xs font-semibold text-[#8b6f47]">
                          {leader.reactionCount}回
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-xl bg-white px-3 py-4 text-sm text-[#6b5540]">
                      直近30日であなたの投稿へのリアクションはまだありません。
                    </div>
                  )}
                </div>
                {discordReactionEmojis.length > 0 && (
                  <div className="mt-auto border-t border-[#efe5d8] pt-3">
                    <p className="mb-2 text-xs text-[#6b5540]">押されたスタンプ</p>
                    <div className="grid grid-cols-5 gap-1.5">
                      {discordReactionEmojis.slice(0, 5).map((emoji, index) => {
                        const customEmojiId =
                          emoji.emojiId != null && String(emoji.emojiId).trim() !== ""
                            ? String(emoji.emojiId).trim()
                            : null;
                        const customEmojiUrl = customEmojiId
                          ? `https://cdn.discordapp.com/emojis/${encodeURIComponent(customEmojiId)}.png?size=48&quality=lossless`
                          : null;

                        return (
                          <div
                            key={`${emoji.id || "emoji"}-${index}`}
                            className="min-w-0 rounded-full bg-white px-2 py-1.5 text-center text-[11px] font-semibold text-[#5d4529] ring-1 ring-[#efe5d8]"
                            title={emoji.emojiName || emoji.emojiDisplay || undefined}
                          >
                            {customEmojiUrl ? (
                              <img
                                src={customEmojiUrl}
                                alt={emoji.emojiName || "stamp"}
                                className="mx-auto h-5 w-5 object-contain"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <span className="block truncate">{emoji.emojiDisplay}</span>
                            )}
                            <span className="block truncate text-[#8a775f]">{emoji.reactionCount}回</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          </MainCampusOnly>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-12"
        >
          <SectionHeader title={t("newArrivals")} icon={Clock} linkTo="Catalog" />
          {newVideosLoading ? (
            <div className={HOME_VIDEO_SCROLL_CLASS}>
              {[...Array(HOME_VIDEO_PREVIEW_COUNT)].map((_, i) => (
                <div key={i} className={HOME_VIDEO_SCROLL_ITEM_CLASS}>
                  <VideoSkeleton />
                </div>
              ))}
            </div>
          ) : (
            <div className={HOME_VIDEO_SCROLL_CLASS}>
              {newVideos.map((video) => (
                <div key={video.id} className={HOME_VIDEO_SCROLL_ITEM_CLASS}>
                  <VideoCard video={video} progress={progressByVideoId.get(String(video.id))} />
                </div>
              ))}
            </div>
          )}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <SectionHeader title={t("continueWatching")} icon={PlayCircle} linkTo="Catalog" />
          {continueWatchingLoading ? (
            <div className={HOME_CONTINUE_SCROLL_CLASS}>
              {[...Array(HOME_VIDEO_PREVIEW_COUNT)].map((_, i) => (
                <div key={i} className={HOME_CONTINUE_SCROLL_ITEM_CLASS}>
                  <VideoSkeleton />
                </div>
              ))}
            </div>
          ) : continueWatching.length > 0 ? (
            <div className={HOME_CONTINUE_SCROLL_CLASS}>
              {continueWatching.map((video) => (
                <div key={video.id} className={HOME_CONTINUE_SCROLL_ITEM_CLASS}>
                  <VideoCard video={video} progress={video.progress} />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-[#e8dccd] bg-white px-4 py-6 text-sm text-[#6b5540]">
              視聴中の動画はまだありません。
            </div>
          )}
        </motion.section>

      </div>
    </div>
  );
}
